import { FormSheet } from '@/shared/components';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { PropertyGroupResponse } from '@/shared/api';
import { ObjectAddressFields } from './ObjectAddressFields';
import {
  addressDefaults,
  addressPatch,
  addressSchema,
  type AddressValues,
} from './objectEditForms';
import { useObjectUpdate } from './useObjectUpdate';

/**
 * Name und Adresse des Objekts. Gibt es die Adresse schon als eigenes Objekt, führt der Server
 * beide zusammen — der Bereich öffnet sich dann am überlebenden Objekt.
 */
export function ObjectAddressSheet({
  group,
  onClose,
}: {
  group: PropertyGroupResponse;
  onClose: () => void;
}) {
  const [initial] = useState(() => addressDefaults(group));
  const form = useForm<AddressValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: initial,
  });
  const { save, saving, error } = useObjectUpdate(
    group,
    '/property-groups/$id/stammdaten',
    onClose,
  );
  const needsReview = form.watch('needsAddressReview');

  return (
    <FormSheet
      title="Name und Adresse"
      description="Gilt für alle Apartments dieses Objekts. Gibt es dieselbe Adresse schon als Objekt, werden beide ohne Rückfrage zusammengeführt."
      dirty={form.formState.isDirty}
      saving={saving}
      canSave={form.formState.isDirty}
      error={error}
      onSubmit={form.handleSubmit((values) => save(addressPatch(values, initial)))}
      onClose={onClose}
    >
      <ObjectAddressFields form={form} />
      {needsReview && (
        <p
          role="status"
          className="border-amber/40 bg-amber/10 text-ink rounded-md border p-3 text-sm"
        >
          Von Hand eingetragen: Nach dem Speichern sucht der Kartendienst die Position. Findet er
          genau dieses Haus, gilt die Adresse als geprüft – sonst setzen Sie den Pin unter „Lage“.
        </p>
      )}
    </FormSheet>
  );
}
